import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import CryptoJS from 'crypto-js'
import axios from 'axios'
import { useSnackbar } from '../../context/popup'

const AuthLoader = ({
  text = 'Insurance Platform Launcher',
  colorGradient = ['#3633e0', '#6292ec', '#00ad9f', '#2ee6c7'],
  size = 40,
  background = 'rgba(255,255,255,0.95)',
  duration = 2,
  gap = 0,
  barWidth = 720
}) => {
  const letters = Array.from(text)
  const navigate = useNavigate()
  const location = useLocation();
  const {showSnackbar} = useSnackbar();
  const SECRET_KEY = '1234'
  const DEFAULT_ROUTE = '/dashboard'

  // inject animations
  useEffect(() => {
    const styleEl = document.createElement('style')

    document.head.appendChild(styleEl)
    const sheet = styleEl.sheet

    const keyframes = [
      `@keyframes hf-flip {
        0% { transform: perspective(400px) rotateX(0deg) translateY(0); opacity: 1; }
        28% { transform: perspective(400px) rotateX(12deg) translateY(-6%); opacity: 0.95; }
        50% { transform: perspective(400px) rotateX(180deg) translateY(-34%); opacity: 0.5; }
        72% { transform: perspective(400px) rotateX(10deg) translateY(-4%); opacity: 0.95; }
        100% { transform: perspective(400px) rotateX(0deg) translateY(0); opacity: 1; }
      }`,
      `@keyframes hf-message-pulse {
        0% { transform: translateY(0); opacity: 0.9; }
        50% { transform: translateY(-4px); opacity: 1; }
        100% { transform: translateY(0); opacity: 0.9; }
      }`,
      `@keyframes gradient-shift {
        0% { background-position: 0% 50%; }
        100% { background-position: 200% 50%; }
      }`
    ]

    keyframes.forEach(rule => sheet.insertRule(rule, sheet.cssRules.length))

    return () => {
      document.head.removeChild(styleEl)
    }
  }, [])

  // Auto login
  useEffect(() => {
    const handleAutoLogin = async () => {
      const params = new URLSearchParams(location.search)
      
      const encryptedData = params.get('data');
      const type = params.get('module') || '';
      
      if (encryptedData) {
        try {
          const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY)
          const creds = JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
          
          setTimeout(() => {
            onSubmit(creds.moduleId, creds.token, type)
            fetchWaffleApps(creds.moduleId, creds.token)
          }, 1500)
        } catch (err) {
          console.error('Decryption failed:', err)
        }
      }
    }

    handleAutoLogin()
  }, [location.search])

  const onSubmit = async (moduleId, token, type) => {

    const identityUrl = process.env.REACT_APP_PUBLIC_AUTHENTICATION;
    try {
      const res = await axios.get(
        `${identityUrl}/access-control/user-role/user/${moduleId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      const data = res.data?.data;
      sessionStorage.setItem('token', token || '')
      
      if (data?.user) {
        // Store session manually
        
        sessionStorage.setItem('user', JSON.stringify(data.user))

        const redirectPath = type?.trim() ? type : DEFAULT_ROUTE
        
        navigate(redirectPath, { replace: true })
      } else {
        showSnackbar("Login Failed","error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Network Issue","error")
    }
  };

  const identityUrl = process.env.REACT_APP_WAFFLE_API_BASE_URL

  const fetchWaffleApps = async (moduleId, token) => {

    try {
      if (!identityUrl) {
        throw new Error('NEXT_PUBLIC_LOGIN_API_BASE_URL is not defined')
      }

      // Call actual authentication API
      const response = await fetch(`${identityUrl}${moduleId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })

      const apiResponse = await response.json()

      const modulesSafe = apiResponse?.data?.base_roles?.map((item) => {
      const payload = {
        moduleId: item?.base_module_id,
        token,
      };

      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(payload),
        SECRET_KEY
      ).toString();

      const redirectUrl = `${item?.app_domain}?data=${encodeURIComponent(
        encrypted
      )}&module=${item?.base_module_routes}`;

      return {
        base_module_name: item?.base_module_name,
        base_module_routes: item?.base_module_routes,

        //  only safe values stored
        encryptedData: encrypted,
        redirectUrl: redirectUrl,
      };
    });

    sessionStorage.setItem(
      "WaffleModules",
      JSON.stringify(modulesSafe)
    );
    } catch (error) {
      console.error('Error Fetching Application', error)
    }
  };

  const gradientCSS = `linear-gradient(120deg, ${colorGradient.join(', ')})`

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background,
        zIndex: 9999
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', gap }}>
          {letters.map((ch, idx) => {
            const delay = (idx * 0.12).toFixed(2)

            return (
              <span
                key={idx}
                style={{
                  display: 'inline-block',
                  fontWeight: 700,
                  fontSize: size,
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  backgroundImage: gradientCSS,
                  backgroundSize: '200% 200%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  animation: `hf-flip ${duration}s cubic-bezier(.2,.9,.2,1) infinite, gradient-shift 3s linear infinite`,
                  animationDelay: `${delay}s, 0s`
                }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            )
          })}
        </div>

        <div
          style={{
            width: barWidth,
            height: 8,
            marginTop: 12,
            borderRadius: 99,
            backgroundImage: gradientCSS,
            animation: 'gradient-shift 4s linear infinite'
          }}
        />
      </div>
    </div>
  )
}

export default AuthLoader